import { motion } from "framer-motion";
import { useT } from "../lang";

const T = {
  pt: {
    onair: "on air · brooklyn",
    kicker: "jornalista · criativa · estrategista",
    tagline:
      "Transformo tendências, conversas e comportamento digital em estratégia criativa.",
    ctaWork: "ver trabalhos →",
    ctaContact: "conversar",
    caption: "brooklyn, ny — 2025",
    portrait: "gf · portrait",
    marquee: ["✷ Estratégia", "✷ Redação", "✷ Social", "✷ Editorial", "✷ Campanhas"],
  },
  en: {
    onair: "on air · brooklyn",
    kicker: "journalist · creative · strategist",
    tagline:
      "I turn trends, conversations, and digital behavior into creative strategy.",
    ctaWork: "see work →",
    ctaContact: "let's talk",
    caption: "brooklyn, ny — 2025",
    portrait: "gf · portrait",
    marquee: ["✷ Strategy", "✷ Copy", "✷ Social", "✷ Editorial", "✷ Campaigns"],
  },
};

export default function Hero() {
  const t = useT(T);
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-16 md:pt-14 overflow-hidden"
    >
      <div className="grid grid-cols-12 md:min-h-[calc(100svh-56px)]">
        {/* Left: terracotta cover */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
          className="col-span-12 md:col-span-7 panel relative flex flex-col justify-between p-8 md:p-14"
          data-testid="hero-cover"
          style={{ minHeight: "60svh" }}
        >
          {/* Top meta bar */}
          <div className="flex items-center justify-between text-off">
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center gap-2 border border-off/60 px-2 py-1"
                style={{ fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}
              >
                <span className="w-1.5 h-1.5 bg-off rounded-full animate-pulse" />
                {t.onair}
              </span>
            </div>
          </div>

          {/* Center display */}
          <div className="py-10 md:py-16">
            <div
              className="text-off/85 mb-6 flex items-center gap-4"
              style={{ fontFamily: "'Archivo', sans-serif", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}
            >
              <span className="text-2xl md:text-3xl">☺</span>
              <span>{t.kicker}</span>
              <span className="text-2xl md:text-3xl">☻</span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.9 }}
              className="font-display uppercase text-off text-[16vw] md:text-[9.5vw] leading-[0.85]"
              data-testid="hero-name"
            >
              GABRIELA
              <br />
              FERRAZ.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.9 }}
              className="mt-8 md:mt-10 font-body text-off text-lg md:text-2xl leading-snug max-w-[620px]"
              data-testid="hero-tagline"
            >
              {t.tagline}
            </motion.p>
          </div>

          {/* Bottom row: CTAs */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.8 }}
              className="flex flex-wrap gap-3"
            >
              <button
                onClick={() =>
                  document
                    .getElementById("portfolio")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center gap-2 px-5 py-3 bg-off text-ink border border-off font-semibold text-sm hover:bg-ink hover:text-off hover:border-ink transition-colors"
                data-testid="hero-cta-work"
                style={{ textTransform: "lowercase", fontFamily: "'Archivo', sans-serif" }}
              >
                {t.ctaWork}
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("contact")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center gap-2 px-5 py-3 bg-transparent text-off border border-off font-semibold text-sm hover:bg-off hover:text-ink transition-colors"
                data-testid="hero-cta-contact"
                style={{ textTransform: "lowercase", fontFamily: "'Archivo', sans-serif" }}
              >
                {t.ctaContact}
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Right: B&W portrait full-bleed */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1], delay: 0.1 }}
          className="col-span-12 md:col-span-5 relative overflow-hidden"
          data-testid="hero-portrait-wrap"
          style={{ minHeight: "50svh" }}
        >
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_meme-pro-design/artifacts/7kxo7i1b_Screen%20Shot%202025-03-08%20at%2010.16.50%20PM.png"
            alt="Gabriela Ferraz"
            data-testid="hero-portrait"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.05)" }}
          />

          <div className="absolute top-4 md:top-6 right-4 md:right-6 flex items-center gap-2 bg-off/95 backdrop-blur px-3 py-2 border border-ink">
            <span
              style={{ fontFamily: "'Archivo', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
              {t.portrait}
            </span>
            <span className="text-brick">☺</span>
          </div>

          <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 flex items-center gap-3 bg-ink text-off px-3 py-2">
            <span style={{ fontFamily: "'Archivo Narrow', sans-serif", fontSize: 11 }}>
              {t.caption}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Marquee bar */}
      <div
        className="border-t border-[color:var(--ink)] bg-off overflow-hidden py-3"
        aria-hidden
      >
        <div
          className="whitespace-nowrap animate-[scroll_28s_linear_infinite]"
          style={{
            display: "inline-block",
            fontFamily: "'Archivo', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i}>
              {t.marquee.map((m, j) => (
                <span key={j} className="mx-6">{m}</span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>
  );
}
